import type { AnalyticsActivityEntry, AnalyticsAssistantTurn } from "@/app/analytics/analyticsTurnState";
import Markdown from "@/components/common/Markdown";

interface AnalyticsAssistantTurnCardProps {
  turn: AnalyticsAssistantTurn;
  nowMs: number;
  isStreaming?: boolean;
}

export default function AnalyticsAssistantTurnCard({
  turn,
  nowMs,
  isStreaming = false,
}: AnalyticsAssistantTurnCardProps) {
  const elapsedMs = (turn.completedAt ?? nowMs) - turn.startedAt;
  const elapsedLabel = formatElapsed(elapsedMs);
  const shouldShowActivity = isStreaming || turn.activityEntries.length > 0;

  return (
    <div className="self-start w-full max-w-[90%] rounded-xl border border-orange-200 bg-white p-4 text-sm text-slate-800 shadow-sm">
      {shouldShowActivity && (
        <details open={isStreaming} className="rounded-lg border border-slate-200 bg-slate-50">
          <summary className="cursor-pointer list-none px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Activity</span>
                {isStreaming && <span className="h-2 w-2 rounded-full bg-orange-400" />}
              </div>
              <span className="font-mono text-xs text-slate-500">{elapsedLabel}</span>
            </div>
          </summary>

          <div className="border-t border-slate-200 px-4 py-3">
            {turn.activityEntries.length === 0 ? (
              <div className="text-sm text-slate-500">Waiting for raw activity...</div>
            ) : (
              <div className="flex flex-col gap-3">
                {turn.activityEntries.map((entry) => (
                  <ActivityEntryView key={entry.id} entry={entry} />
                ))}
              </div>
            )}
          </div>
        </details>
      )}

      <div className={shouldShowActivity ? "mt-4" : ""}>
        {turn.finalAnswer ? (
          <div className="prose prose-sm prose-neutral max-w-none prose-img:m-0">
            <Markdown
              gfm
              components={{
                a: ({ href, children }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    {children}
                  </a>
                ),
                table: ({ children }) => (
                  <div className="my-2 overflow-x-auto">
                    <table className="min-w-full border-collapse border border-slate-300 text-sm">{children}</table>
                  </div>
                ),
                thead: ({ children }) => <thead className="bg-slate-100">{children}</thead>,
                th: ({ children }) => (
                  <th className="border border-slate-300 px-3 py-2 text-left font-medium">{children}</th>
                ),
                td: ({ children }) => <td className="border border-slate-300 px-3 py-2">{children}</td>,
              }}
            >
              {turn.finalAnswer}
            </Markdown>
          </div>
        ) : turn.status === "cancelled" ? (
          <div className="text-sm italic text-slate-500">Stopped.</div>
        ) : turn.error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700">{turn.error}</div>
        ) : (
          <div className="text-sm text-slate-500">
            {isStreaming ? "Waiting for the final answer..." : "No answer returned."}
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityEntryView({ entry }: { entry: AnalyticsActivityEntry }) {
  if (entry.kind === "output") {
    return (
      <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-900/95 px-3 py-2 font-mono text-xs text-slate-100">
        {entry.text}
      </pre>
    );
  }

  if (entry.kind === "command") {
    return (
      <div className="rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-700">
        <div className="mb-1 flex items-center justify-between gap-3 font-sans">
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Command</span>
          {entry.status && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.08em] text-slate-600">
              {entry.status}
            </span>
          )}
        </div>
        <div className="whitespace-pre-wrap break-all">{entry.text}</div>
      </div>
    );
  }

  return <div className="whitespace-pre-wrap text-sm text-slate-600">{entry.text}</div>;
}

function formatElapsed(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
